import { Util } from "@auvious/common";
import {
  INotification,
  INotificationOptions,
  ConfirmNotification,
} from "../models";
import { dispatchShow, dispatchDismiss } from "../store/actions/notification";
import { store } from "@stencil/redux";
import { AVBusService } from "./bus.service";
import { HandledError } from "../models/HandledError";
import { Log } from "./log.service";
import { NotificationTypeEnum } from "../models/enums";
import { Subject, Observable } from "rxjs";
import { AppConfigService } from "./app.config.service";

class NotificationServiceCtrl {
  dispatchShow: typeof dispatchShow;
  dispatchDismiss: typeof dispatchDismiss;

  clickedSubject: Subject<INotification> = new Subject();
  openedSubject: Subject<INotification> = new Subject();

  notificationMap: Map<string, NotificationTypeEnum> = new Map();

  voiceOverAnnouncer: (message: string) => void;

  constructor() {
    store.mapDispatchToProps(this, {
      dispatchShow,
      dispatchDismiss,
    });
  }

  error(title: string, options?: INotificationOptions): string {
    if (!!options?.errorCode) {
      let error;
      if (options?.error) {
        error = options.error;
      } else {
        error = new Error(options?.body || title);
        error.name = title;
      }
      AVBusService.errorLog(new HandledError(error, options?.errorCode));
    }
    return this.sendNotification(NotificationTypeEnum.error, title, options);
  }

  info(title: string, options?: INotificationOptions): string {
    return this.sendNotification(NotificationTypeEnum.info, title, options);
  }

  warning(title: string, options?: INotificationOptions): string {
    return this.sendNotification(NotificationTypeEnum.warning, title, options);
  }

  success(title: string, options?: INotificationOptions): string {
    return this.sendNotification(NotificationTypeEnum.success, title, options);
  }

  greeting(title: string, options?: INotificationOptions): string {
    return this.sendNotification(NotificationTypeEnum.greeting, title, options);
  }

  dismiss(id: string) {
    this.dispatchDismiss(id);
    if (this.notificationMap.get(id) === NotificationTypeEnum.confirm) {
      this.notificationMap.delete(id);
    }
  }

  confirm(title: string, options?: INotificationOptions) {
    if (!this.isConfirmOpen) {
      const id = this.sendNotification(
        NotificationTypeEnum.confirm,
        title,
        options
      );
      this.notificationMap.set(id, NotificationTypeEnum.confirm);
      return id;
    }
  }

  confirmComplete(id: string) {
    this.notificationMap.delete(id);
  }

  broadcastClicked(notification: INotification) {
    this.clickedSubject.next(notification);
  }

  broadcastOpened(notification: INotification) {
    this.openedSubject.next(notification);
  }

  get isConfirmOpen() {
    return (
      Array.from(this.notificationMap.values()).filter(
        (t) => t === NotificationTypeEnum.confirm
      ).length > 0
    );
  }

  get clicked$(): Observable<INotification> {
    return this.clickedSubject.asObservable();
  }

  get opened$(): Observable<INotification> {
    return this.openedSubject.asObservable();
  }

  public async playNotificationSound() {
    const sound = new Audio(AppConfigService.getNotificationSoundUrl());
    try {
      await sound.play();
    } catch (ex) {
      Log.error(ex);
    }
  }

  public announceVoiceOverMessage(message: string) {
    this.voiceOverAnnouncer?.(message);
  }

  public registerVoiceOverAnnouncer(cb: (message: string) => void) {
    this.voiceOverAnnouncer = cb;
  }

  private sendNotification(
    type: NotificationTypeEnum,
    title,
    options?: INotificationOptions
  ): string {
    if (options?.sound) {
      this.playNotificationSound();
    }
    let notification: INotification;
    if (type === NotificationTypeEnum.confirm) {
      notification = new ConfirmNotification(title, options);
    } else {
      let nuOptions;
      if (!options || !options.ttl || !options.body) {
        nuOptions = {
          ...options,
          ttl: options?.ttl ?? 5000,
          body: options?.body ?? title,
        };
      }
      notification = {
        id: Util.uuidgen(),
        type,
        title: !!options?.body ? title : null,
        options: nuOptions || options,
      };
    }
    this.broadcastOpened(notification);
    this.dispatchShow(notification);
    return notification.id;
  }
}

export const NotificationService = new NotificationServiceCtrl();
